import type { MouseEvent } from 'react';
import { fmtPln } from './client-offer-review-format';
import {
  ClientOfferReviewLargePhotoBlock,
  ClientOfferReviewSmallPhotoBlock,
} from './client-offer-review-photo-blocks';
import type {
  ClientOfferReviewDecision,
  ClientOfferReviewPeriod,
  ClientOfferReviewUnit,
  ClientOfferReviewUnitCardLabels,
} from './client-offer-review-types';
import {
  UNIT_CARD_BADGE_CLASS,
  UNIT_CARD_STRIPE_CLASS,
  unitCardBorderClassName,
  unitPriceModel,
  unitReviewStatus,
} from './client-offer-review-unit-card-model';
import { UnitCardExpandedPanel } from './client-offer-review-unit-card-expanded-panel';
import { UnitCardHeaderRow } from './client-offer-review-unit-card-header-row';

export type StopFn = (fn: () => void) => (e: MouseEvent) => void;
export type CardBadge = { label: string; className: string } | null;

interface ClientOfferReviewUnitCardProps {
  unit: ClientOfferReviewUnit;
  lp: string;
  totalCount: number;
  decision: ClientOfferReviewDecision;
  photoMode: 'large' | 'small' | 'none';
  periods: ClientOfferReviewPeriod[];
  selectedPeriodIds: string[];
  active: boolean;
  pulsing: boolean;
  readOnly: boolean;
  roHint: string;
  onLocate: () => void;
  onOpenDetail: () => void;
  onOpenLightbox: () => void;
  onAccept: () => void;
  onReject: () => void;
  /** Toggles a free chip on this unit's period strip — see ClientOfferReviewPeriodChips gating. */
  onTogglePeriod?: (periodId: string, nowSelected: boolean) => void;
  rowRef: (el: HTMLDivElement | null) => void;
  labels: ClientOfferReviewUnitCardLabels;
}

function badgeFor(
  unit: ClientOfferReviewUnit,
  decision: ClientOfferReviewDecision,
  labels: ClientOfferReviewUnitCardLabels,
): CardBadge {
  if (unit.sold) return { label: labels.soldLabel, className: UNIT_CARD_BADGE_CLASS.sold };
  if (decision === 'accepted')
    return { label: labels.acceptedBadge, className: UNIT_CARD_BADGE_CLASS.accepted };
  if (decision === 'rejected')
    return { label: labels.rejectedBadge, className: UNIT_CARD_BADGE_CLASS.rejected };
  return null;
}

/** One unit card — header row + photo variant + always-visible detail panel. */
export function ClientOfferReviewUnitCard(props: ClientOfferReviewUnitCardProps) {
  const {
    unit,
    lp,
    totalCount,
    decision,
    photoMode,
    periods,
    selectedPeriodIds,
    active,
    pulsing,
    readOnly,
    roHint,
    onLocate,
    onOpenDetail,
    onOpenLightbox,
    onAccept,
    onReject,
    onTogglePeriod,
    rowRef,
    labels,
  } = props;

  const status = unitReviewStatus(unit, decision);
  const grayFilter = status === 'rejected';
  const badge = badgeFor(unit, decision, labels);
  const price = unitPriceModel(
    unit,
    grayFilter,
    fmtPln,
    labels.priceConfirmationPending,
    labels.netSuffix,
  );

  const stop: StopFn = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };

  return (
    <div
      ref={rowRef}
      data-testid={`unit-card-${unit.id}`}
      // Whole-card click mirrors the existing locate button (CU-86cbc55rv): clicking
      // anywhere on the card's non-interactive surface flies the map to the unit and
      // marks it active, same as clicking its map pin marks the card active. Nested
      // interactive controls stay opt-out via `stop` (below) so they keep their own
      // single behavior instead of also re-triggering locate on bubble.
      onClick={onLocate}
      className={`shrink-0 cursor-pointer overflow-hidden rounded-xl border transition-shadow ${unitCardBorderClassName(unit, decision, active ? unit.id : null)} ${unit.sold ? 'bg-status-unavailable/10 opacity-70' : 'bg-card'} ${pulsing ? 'ring-2 ring-primary/45' : ''}`}
    >
      <div className="flex items-stretch">
        <div className={`w-1 shrink-0 ${UNIT_CARD_STRIPE_CLASS[status]}`} />
        {/*
         * Both photo sizes render as a LEFT column (otodom.pl-style listing
         * card: photo left, params right) — they only differ in width, not
         * placement, so there's a single conditional spot instead of one
         * mode living outside the row as a full-width top banner.
         */}
        {photoMode === 'large' ? (
          <ClientOfferReviewLargePhotoBlock
            photoUrl={unit.thumbnailUrl}
            // unit.dimensions is already fully formatted ("{width} x {height} m")
            // by the server — appending " m" here double-suffixed it.
            formatLine={`${unit.format} · ${unit.dimensions}`}
            lp={lp}
            totalCount={totalCount}
            sold={unit.sold}
            badge={badge}
            onOpen={stop(onOpenLightbox)}
            grayFilter={grayFilter}
            labels={labels.photoBlock}
          />
        ) : null}
        {photoMode === 'small' ? (
          <ClientOfferReviewSmallPhotoBlock
            photoUrl={unit.thumbnailUrl}
            sold={unit.sold}
            onOpen={stop(onOpenLightbox)}
            grayFilter={grayFilter}
            labels={labels.photoBlock}
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-1.5 px-3 py-2.5">
          <UnitCardHeaderRow
            unit={unit}
            badge={badge}
            photoMode={photoMode}
            price={price}
            grayFilter={grayFilter}
          />

          <UnitCardExpandedPanel
            unit={unit}
            photoMode={photoMode}
            showKlocki={!unit.sold}
            periods={periods}
            selectedPeriodIds={selectedPeriodIds}
            decision={decision}
            readOnly={readOnly}
            roHint={roHint}
            grayFilter={grayFilter}
            onOpenDetail={onOpenDetail}
            onOpenLightbox={onOpenLightbox}
            onAccept={onAccept}
            onReject={onReject}
            onTogglePeriod={onTogglePeriod}
            labels={labels}
            stop={stop}
          />
        </div>
      </div>
    </div>
  );
}
